import { useState, useEffect, useRef } from "react";
import { PrayerTimes } from "../types/PrayerTypes";
import { ScreenData } from "../types/ScreenTypes";
import { getPrayerLength } from "../utils/prayerTimeUtils";

interface AfterPrayerSalahChangesState {
  /** Whether the after-prayer salah changes overlay should be active */
  isActive: boolean;
  /** Which prayer triggered the overlay (e.g. "JamDhuhr") */
  activePrayer: string | null;
  /** Seconds remaining until the overlay auto-dismisses */
  remainingSeconds: number;
}

// The 4 prayers we care about — NOT Maghrib, NOT Jummah
const TARGET_PRAYERS = ["JamFajr", "JamDhuhr", "JamAsar", "JamEsha"] as const;

function parseTimeToDate(timeStr: string): Date | null {
  if (!timeStr || timeStr === "--:--") return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Hook that detects when a prayer has just ended and that prayer's jamaat time
 * is changing tomorrow. Returns overlay activation state.
 *
 * Only checks: JamFajr, JamDhuhr, JamAsar, JamEsha.
 * Skips JamDhuhr on Fridays.
 */
export function useAfterPrayerSalahChanges(
  prayerTimes: PrayerTimes | null,
  tomorrowPrayerTimes: PrayerTimes | null,
  screenData: ScreenData | null,
  enabled: boolean,
): AfterPrayerSalahChangesState {
  const [state, setState] = useState<AfterPrayerSalahChangesState>({
    isActive: false,
    activePrayer: null,
    remainingSeconds: 0,
  });

  // Track which prayer we've already shown for this cycle to avoid re-triggering
  const shownForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !prayerTimes || !tomorrowPrayerTimes || !screenData) {
      if (state.isActive) {
        setState({ isActive: false, activePrayer: null, remainingSeconds: 0 });
      }
      return;
    }

    const duration = screenData.salahChangesAfterPrayerDuration ?? 30;
    const isFriday = new Date().getDay() === 5;

    const checkInterval = setInterval(() => {
      const now = new Date();

      for (const prayerKey of TARGET_PRAYERS) {
        // Skip JamDhuhr on Fridays
        if (prayerKey === "JamDhuhr" && isFriday) continue;

        const todayTime = prayerTimes[prayerKey as keyof PrayerTimes] as string;
        const tomorrowTime = tomorrowPrayerTimes[
          prayerKey as keyof PrayerTimes
        ] as string;

        // Skip if either time is missing
        if (
          !todayTime ||
          !tomorrowTime ||
          todayTime === "--:--" ||
          tomorrowTime === "--:--"
        )
          continue;

        // Only trigger if tomorrow's time is DIFFERENT from today's
        if (todayTime === tomorrowTime) continue;

        // Calculate after-prayer window
        const jamaatDate = parseTimeToDate(todayTime);
        if (!jamaatDate) continue;

        const prayerLengthSec = getPrayerLength(prayerKey, screenData);
        const windowStart = new Date(
          jamaatDate.getTime() + prayerLengthSec * 1000,
        );
        const windowEnd = new Date(windowStart.getTime() + duration * 1000);

        if (now >= windowStart && now <= windowEnd) {
          const remaining = Math.ceil(
            (windowEnd.getTime() - now.getTime()) / 1000,
          );

          // Only activate if we haven't already shown for this prayer in this window
          if (shownForRef.current !== prayerKey) {
            shownForRef.current = prayerKey;
          }

          setState({
            isActive: true,
            activePrayer: prayerKey,
            remainingSeconds: remaining,
          });
          return; // First matching prayer wins
        }
      }

      // No prayer in after-window — deactivate
      if (state.isActive || shownForRef.current) {
        // Reset shown tracker when we leave all windows
        const inAnyWindow = TARGET_PRAYERS.some((key) => {
          if (key === "JamDhuhr" && isFriday) return false;
          const todayTime = prayerTimes[key as keyof PrayerTimes] as string;
          if (!todayTime || todayTime === "--:--") return false;
          const jamaatDate = parseTimeToDate(todayTime);
          if (!jamaatDate) return false;
          const prayerLengthSec = getPrayerLength(key, screenData);
          const windowEnd = new Date(
            jamaatDate.getTime() + (prayerLengthSec + duration) * 1000,
          );
          return now <= windowEnd && now >= jamaatDate;
        });

        if (!inAnyWindow) {
          shownForRef.current = null;
        }
      }

      setState({ isActive: false, activePrayer: null, remainingSeconds: 0 });
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [enabled, prayerTimes, tomorrowPrayerTimes, screenData, state.isActive]);

  return state;
}
